// src/pages/UploadPreviewTable.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../components/common/Header";
import Footer from "../components/common/Footer";

/** UTILITIES **/
const getVal = (obj, ...keys) => {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return "";
};

// turn “Invalid …; Missing required: …” into a short label for tooltip
const reasonShort = (msg) => (msg ? msg.split(";")[0].trim() : "");

/** MAIN PAGE **/
export default function UploadPreviewTable() {
  const { state } = useLocation(); // expects { preview, file } from upload validate page
  const navigate = useNavigate();

  const preview = state?.preview || {
    totalRows: 0,
    validRows: 0,
    invalidRows: 0,
    inserted: 0,
    validRecords: [],
    invalidRecords: [],
    summaryMessage: "",
  };

  // Build merged rows once
  const mergedRows = useMemo(() => {
    const v = (preview.validRecords || []).map((r) => ({
      ...r,
      __status: "Valid",
      errorMessage: "",
    }));
    const i = (preview.invalidRecords || []).map((r) => ({
      ...r,
      __status: "Invalid",
      errorMessage: r.errorMessage ?? r.ErrorMessage ?? "",
    }));
    // sort by rowNumber asc
    return [...v, ...i].sort((a, b) => (a.rowNumber ?? 0) - (b.rowNumber ?? 0));
  }, [preview]);

  // ---------- Filters & search ----------
  const [q, setQ] = useState("");
  const [fltProperty, setFltProperty] = useState("All");
  const [fltInspector, setFltInspector] = useState("All"); // we’ll map to “email” to mirror the screenshot
  const [fltDateRange] = useState("All"); // placeholder to match the UI (no dates in payload)
  const [fltStatus, setFltStatus] = useState("All");

  const propertyOptions = useMemo(() => {
    const s = new Set(
      mergedRows.map((r) =>
        getVal(
          r.residentDetails || r.ResidentDetails || {},
          "propertyName",
          "PropertyName"
        )
      )
    );
    return ["All", ...[...s].filter(Boolean)];
  }, [mergedRows]);

  const inspectorOptions = useMemo(() => {
    const s = new Set(
      mergedRows.map((r) =>
        getVal(r.residentDetails || r.ResidentDetails || {}, "email", "Email")
      )
    );
    return ["All", ...[...s].filter(Boolean)];
  }, [mergedRows]);

  const statusOptions = ["All", "Valid", "Invalid"];

  const filtered = useMemo(() => {
    const qry = q.trim().toLowerCase();
    return mergedRows.filter((r) => {
      const d = r.residentDetails || r.ResidentDetails || {};
      const name = `${getVal(d, "firstName", "FirstName")} ${getVal(
        d,
        "lastName",
        "LastName"
      )}`.toLowerCase();
      const prop = getVal(d, "propertyName", "PropertyName").toLowerCase();
      const email = getVal(d, "email", "Email").toLowerCase();
      const city = getVal(d, "city", "City").toLowerCase();
      const anyMatch =
        !qry ||
        name.includes(qry) ||
        prop.includes(qry) ||
        email.includes(qry) ||
        city.includes(qry);

      const propertyMatch =
        fltProperty === "All" || prop === fltProperty.toLowerCase();

      const inspectorMatch =
        fltInspector === "All" || email === fltInspector.toLowerCase();

      const statusMatch = fltStatus === "All" || r.__status === fltStatus;

      // date range omitted (no date in payload) => treat as always match
      return anyMatch && propertyMatch && inspectorMatch && statusMatch;
    });
  }, [mergedRows, q, fltProperty, fltInspector, fltStatus]);

  // ---------- Pagination ----------
  const PAGE_SIZE = 5;
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const start = (page - 1) * PAGE_SIZE;
  const slice = filtered.slice(start, start + PAGE_SIZE);

  const showingFrom = filtered.length === 0 ? 0 : start + 1;
  const showingTo = start + slice.length;

  // ---------- Styles (lightweight, to mimic screenshot) ----------
  const styles = {
    wrap: { padding: "24px 32px" },
    toolbar: {
      display: "flex",
      gap: 12,
      alignItems: "center",
      marginBottom: 16,
      flexWrap: "wrap",
    },
    search: {
      position: "relative",
      flex: "1 1 300px",
      maxWidth: 380,
    },
    searchInput: {
      paddingLeft: 36,
      height: 40,
      borderRadius: 8,
      border: "1px solid #E5E7EB",
      width: "100%",
    },
    searchIcon: {
      position: "absolute",
      left: 10,
      top: 9,
      color: "#FF6A00",
      fontSize: 18,
    },
    pill: {
      height: 40,
      borderRadius: 8,
      border: "1px solid #E5E7EB",
      padding: "0 12px",
      background: "#fff",
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    newBtn: {
      marginLeft: "auto",
      height: 40,
      borderRadius: 8,
      background: "#0B1B3B",
      color: "#fff",
      border: "none",
      padding: "0 16px",
      fontWeight: 600,
    },
    tableWrap: {
      background: "#fff",
      border: "1px solid #E5E7EB",
      borderRadius: 12,
      overflow: "hidden",
    },
    table: { width: "100%", borderCollapse: "separate", borderSpacing: 0 },
    th: {
      textTransform: "uppercase",
      fontSize: 12,
      letterSpacing: 0.3,
      color: "#4B5563",
      background: "#F5F7FB",
      padding: "12px 16px",
      borderBottom: "1px solid #E5E7EB",
    },
    td: {
      padding: "14px 16px",
      borderBottom: "1px solid #F1F3F7",
      fontSize: 14,
      color: "#111827",
      verticalAlign: "middle",
    },
    nameBold: { fontWeight: 700 },
    badge: (kind) => ({
      display: "inline-block",
      padding: "4px 10px",
      fontSize: 12,
      borderRadius: 999,
      background: kind === "Valid" ? "#E9FAF0" : "#FFF4E8",
      color: kind === "Valid" ? "#08875D" : "#AA4E00",
      fontWeight: 600,
    }),
    actions: { display: "flex", gap: 14, color: "#EE6B4D" },
    actionIcon: { cursor: "pointer" },
    pagerWrap: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginTop: 12,
    },
    small: { color: "#6B7280", fontSize: 12 },
    pageBtn: (active) => ({
      minWidth: 36,
      height: 34,
      borderRadius: 8,
      background: active ? "#0B1B3B" : "#fff",
      color: active ? "#fff" : "#111827",
      border: "1px solid #E5E7EB",
    }),
  };

  // ---------- Render ----------
  return (
    <>
      <Header />
      <div className="dashboard_body_main">
        <div className="dashboard_body" style={styles.wrap}>
          {/* Toolbar */}
          <div style={styles.toolbar}>
            <div style={styles.search}>
              <i className="fa fa-search" style={styles.searchIcon} />
              <input
                style={styles.searchInput}
                placeholder="Search inspections..."
                value={q}
                onChange={(e) => {
                  setPage(1);
                  setQ(e.target.value);
                }}
              />
            </div>

            {/* Property filter */}
            <div style={styles.pill}>
              <span>Property Name</span>
              <i className="fa fa-angle-down" />
              <select
                value={fltProperty}
                onChange={(e) => {
                  setPage(1);
                  setFltProperty(e.target.value);
                }}
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                }}
              >
                {propertyOptions.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>

            {/* “Inspector” = email to mirror screenshot */}
            <div style={styles.pill}>
              <span>Inspector</span>
              <i className="fa fa-angle-down" />
              <select
                value={fltInspector}
                onChange={(e) => {
                  setPage(1);
                  setFltInspector(e.target.value);
                }}
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                }}
              >
                {inspectorOptions.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>

            {/* Date Range (placeholder – no dates in payload) */}
            <div style={styles.pill}>
              <span>Date Range</span>
              <i className="fa fa-angle-down" />
              <select
                value={fltDateRange}
                onChange={() => {}}
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                }}
              >
                <option>All</option>
              </select>
            </div>

            {/* Status */}
            <div style={styles.pill}>
              <span>Status</span>
              <i className="fa fa-angle-down" />
              <select
                value={fltStatus}
                onChange={(e) => {
                  setPage(1);
                  setFltStatus(e.target.value);
                }}
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                }}
              >
                {statusOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <button
              style={styles.newBtn}
              onClick={() => navigate("/residents/upload", { replace: false })}
            >
              <i className="fa fa-plus me-2" />
              New Upload
            </button>
          </div>

          {/* Table */}
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>PROPERTY NAME</th>
                  <th style={styles.th}>ASSIGNED INSPECTOR</th>
                  <th style={styles.th}>SCHEDULED DATE</th>
                  <th style={styles.th}>STATUS</th>
                  <th style={styles.th}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {slice.map((r, idx) => {
                  const d = r.residentDetails || r.ResidentDetails || {};
                  const prop = getVal(d, "propertyName", "PropertyName");
                  const inspector = getVal(d, "email", "Email");
                  // we don't have a date in payload; show a dash to match structure
                  const scheduled = "—";
                  const status = r.__status === "Valid" ? "Valid" : "Invalid";
                  const err = r.errorMessage ?? r.ErrorMessage ?? "";

                  return (
                    <tr key={`${r.rowNumber}-${idx}`}>
                      <td style={styles.td}>
                        <div style={styles.nameBold}>
                          {prop || "(No property)"}
                        </div>
                      </td>
                      <td style={styles.td}>{inspector || "—"}</td>
                      <td style={styles.td}>{scheduled}</td>
                      <td style={styles.td}>
                        <span
                          style={styles.badge(status)}
                          title={status === "Invalid" ? reasonShort(err) : ""}
                        >
                          {status}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.actions}>
                          <i
                            className="fa fa-pencil"
                            title="Edit"
                            style={styles.actionIcon}
                            onClick={() =>
                              alert(`Edit row #${r.rowNumber || ""}`)
                            }
                          />
                          <i
                            className="fa fa-calendar"
                            title="Schedule"
                            style={styles.actionIcon}
                            onClick={() => alert("Not implemented")}
                          />
                          <i
                            className="fa fa-trash"
                            title="Delete"
                            style={styles.actionIcon}
                            onClick={() => alert("Not implemented")}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {slice.length === 0 && (
                  <tr>
                    <td style={styles.td} colSpan={5}>
                      No results.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pager */}
          <div style={styles.pagerWrap}>
            <div style={styles.small}>
              Showing {showingFrom} to {showingTo} of {filtered.length} results
            </div>

            <div className="d-flex align-items-center gap-2">
              <button
                style={styles.pageBtn(false)}
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>

              {/* Page numbers (compact) */}
              {Array.from({ length: pageCount }).map((_, i) => {
                const n = i + 1;
                // keep it concise like the screenshot (1, 2, 3 … 5)
                if (pageCount > 6) {
                  const near =
                    n === 1 ||
                    n === pageCount ||
                    Math.abs(n - page) <= 1 ||
                    (page <= 3 && n <= 4) ||
                    (page >= pageCount - 2 && n >= pageCount - 3);
                  if (!near) {
                    if (n === 2 || n === pageCount - 1) {
                      return (
                        <span key={n} className="mx-1">
                          …
                        </span>
                      );
                    }
                    return null;
                  }
                }
                return (
                  <button
                    key={n}
                    style={styles.pageBtn(n === page)}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                );
              })}

              <button
                style={styles.pageBtn(false)}
                disabled={page >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
